import { type FC } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';

import { useModal } from '@contexts/modal-context/ModalContext';
import type { LoginFormValues } from '@features/authentication/components/modals/schemas/login.schema';
import { LoginSchema } from '@features/authentication/components/modals/schemas/login.schema';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, Input } from '@shared/components/ui';
import { GenericButton, PasswordInput } from '@shared/components/ui/custom';
import { OiWordmarkLogin } from '@shared/icons';

import type { LoginArgs } from '../api/authentication.api';

interface LoginFormProps {
  onFinish: (values: LoginArgs) => Promise<void>;
  isLoading: boolean;
}

export const LoginForm: FC<LoginFormProps> = ({ onFinish, isLoading }) => {
  const { openModal } = useModal();

  const form = useForm<LoginFormValues>({
    resolver: zodResolver(LoginSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const handleSubmit = ({ email, password }: LoginFormValues) => {
    void onFinish({ email, password });
  };

  return (
    <div
      className="min-h-screen w-full flex items-center justify-center bg-background"
      data-testid="authentication-login-root"
    >
      <div
        className="flex flex-col w-[400px] gap-6 p-8 bg-card border border-border rounded-lg"
        data-testid="authentication-login-card"
      >
        <div className="flex flex-col items-center gap-4" data-testid="authentication-login-branding">
          <div className="w-[170px] text-foreground">
            <OiWordmarkLogin />
          </div>
        </div>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            className="space-y-4 pt-4"
            noValidate
            data-testid="authentication-login-form"
          >
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center gap-2" data-testid="authentication-login-label-email">
                    Email
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      type="email"
                      placeholder="Enter email"
                      data-testid="authentication-login-input-email"
                      autoComplete="Username"
                    />
                  </FormControl>
                  <FormMessage data-test-id="authentication-login-input-email-error" />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center gap-2" data-testid="authentication-login-label-password">
                    Password
                  </FormLabel>
                  <FormControl>
                    <PasswordInput
                      {...field}
                      placeholder="Enter password"
                      autoComplete="current-password"
                      data-testid="authentication-login-input-password"
                    />
                  </FormControl>
                  <FormMessage data-test-id="authentication-login-error-password" />
                </FormItem>
              )}
            />

            <div className="flex justify-end mt-4 mb-6">
              <GenericButton
                type="submit"
                className="w-full"
                disabled={isLoading}
                data-testid="authentication-login-submit-button"
                label="Log in"
              />
            </div>
            <div className="text-center">
              <GenericButton
                label="Forgot Password?"
                type="button"
                variant="link"
                size="sm"
                className="font-normal"
                onClick={() => openModal('FORGOT_PASSWORD')}
                data-testid="authentication-login-forgot-password-button"
              />
            </div>
          </form>
        </Form>
      </div>
    </div>
  );
};
